import {
  Placement,
  size,
  flip,
  limitShift,
  offset,
  shift,
  computePosition,
  autoUpdate,
} from "@floating-ui/react";
import {
  ComponentPropsWithoutRef,
  useEffect,
  useId,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useContext,
  MouseEvent,
  KeyboardEvent,
} from "react";
import styles from "./Menu.module.scss";
import Next from "@servicetitan/hammer-icon/mdi/round/navigate_next.svg";
import Up from "@servicetitan/hammer-icon/mdi/round/arrow_drop_up.svg";
import Down from "@servicetitan/hammer-icon/mdi/round/arrow_drop_down.svg";
import { Icon, IconProps } from "../Icon";
import { MenuContext, MenuCommonProps } from "./Menu";
import cx from "classnames";
import { getActiveElement } from "../../internal";
import {
  safeHidePopover,
  safeShowPopover,
} from "../../internal/functions/safePopover";

/**
 * Props for the MenuNested component
 * @extends ComponentPropsWithoutRef<"button">
 * @extends MenuCommonProps
 */
export type MenuProps = ComponentPropsWithoutRef<"button"> &
  MenuCommonProps & {
    /**
     * Icon to display on the nested menu button
     */
    icon?: IconProps["svg"];
  };

/**
 * MenuNested component for creating nested submenus within a Menu.
 *
 * Features:
 * - Hover-based menu opening and closing
 * - Automatic positioning with fallback strategies
 * - Keyboard navigation with arrow keys
 * - Scroll indicators for overflow content
 * - Automatic height adjustment
 * - Focus management and accessibility
 * - Popover API support with legacy fallback
 * - Smooth transitions and animations
 * - Context integration with parent menus
 *
 * @example
 * <Menu label="Edit">
 *   <Menu.Item label="Cut" onClick={() => console.log('Cut clicked')} />
 *   <Menu label="Copy as">
 *     <Menu.Item label="Text" onClick={() => console.log('Copy as Text')} />
 *     <Menu.Item label="Video" onClick={() => console.log('Copy as Video')} />
 *     <Menu label="Image">
 *       <Menu.Item label=".png" onClick={() => console.log('Copy as PNG')} />
 *       <Menu.Item label=".jpg" onClick={() => console.log('Copy as JPG')} />
 *     </Menu>
 *   </Menu>
 * </Menu>
 */
export const MenuNested = (props: MenuProps) => {
  const { children, open, defaultOpen = false, label, className, icon } = props;
  const uid = useId();
  const [tabIndex, setTabIndex] = useState(-1);
  const [openState, setOpenState] = useState<boolean>(open ?? defaultOpen);
  const [scrollTop, setScrollTop] = useState(0);
  const [menuHeight, setMenuHeight] = useState<number | null>(null);
  const [arrowStart, setArrowStart] = useState(false);
  const [arrowEnd, setArrowEnd] = useState(false);
  const invokerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusItems, setFocusItems] = useState<HTMLElement[]>();
  const menuContext = useContext(MenuContext);

  // TODO: Find a way to test these properly!
  // Stryker disable all : Currently relying on Chromatic test for all visuals
  /* istanbul ignore next */
  const fallbackArr = useCallback(() => {
    return menuContext?.disableAutoHeight
      ? ["right-start"]
      : ["right-start", "right-end", "left-start", "left-end"];
  }, [menuContext?.disableAutoHeight]);

  const updatePosition = useCallback(() => {
    if (!invokerRef.current || !menuRef.current) return;
    const timeoutId = setTimeout(() => {
      if (!invokerRef.current || !menuRef.current) return;
      computePosition(invokerRef.current, menuRef.current, {
        placement: "right-start",
        middleware: [
          offset({
            mainAxis: 0,
            alignmentAxis: -4,
          }),
          flip({
            fallbackStrategy: "bestFit",
            fallbackPlacements: fallbackArr() as Placement[],
          }),
          shift({
            mainAxis: !menuContext?.disableAutoHeight,
            crossAxis: false,
            limiter: limitShift({
              mainAxis: !menuContext?.disableAutoHeight,
            }),
          }),
          size({
            padding: 8,
            apply({ availableWidth, availableHeight, elements }) {
              if (menuContext?.disableAutoHeight) return;
              const newHeight = availableHeight;
              if (Math.abs((menuHeight ?? 0) - newHeight) > 1) {
                setMenuHeight(newHeight);
                Object.assign(elements.floating.style, {
                  maxWidth: `${availableWidth - 16}px`,
                  maxHeight: `${availableHeight - 16}px`,
                });
              }
            },
          }),
        ],
      }).then(({ x, y }) => {
        if (!menuRef.current) return;
        Object.assign(menuRef.current.style, {
          left: `${x}px`,
          top: `${y + 8}px`,
        });
      });
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [fallbackArr, menuContext?.disableAutoHeight, menuHeight]);

  const menuReference = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!openState) {
      setMenuHeight(null);
      setScrollTop(0);
      return;
    }

    if (!menuRef.current) return;
    menuReference.current = menuRef.current;

    let resizeTimeout: number | null = null;
    const lastDimensions = {
      height: menuReference.current.clientHeight,
      scrollHeight: childrenRef.current?.scrollHeight ?? 0,
    };

    const updateArrows = (height: number, scrollHeight: number) => {
      if (!childrenRef.current) return;
      const isOverflow = scrollHeight > height;
      setArrowStart(isOverflow && childrenRef.current.scrollTop > 0);
      setArrowEnd(isOverflow && scrollHeight - height - scrollTop - 16 > 0);
    };

    const observer = new ResizeObserver(() => {
      if (!childrenRef.current || !menuReference.current) return;

      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }

      resizeTimeout = window.setTimeout(() => {
        const newHeight = menuReference.current?.clientHeight ?? 0;
        const newScrollHeight = childrenRef.current?.scrollHeight ?? 0;

        if (!newHeight) return;

        lastDimensions.height = newHeight;
        lastDimensions.scrollHeight = newScrollHeight;

        setMenuHeight(newHeight);
        updateArrows(newHeight, newScrollHeight);
      }, 100); // Shorter debounce for nested menu
    });

    if (menuReference.current) {
      observer.observe(menuReference.current);
    }

    return () => {
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }
      if (menuReference.current) {
        observer.unobserve(menuReference.current);
      }
      observer.disconnect();
    };
  }, [openState, scrollTop]);

  // Effect to add autoUpdate to eventListener
  // while the popover is opened
  useLayoutEffect(() => {
    if (!menuRef.current || !invokerRef.current) return;

    const cleanup = autoUpdate(invokerRef.current, menuRef.current, () => {
      updatePosition();
    });

    return () => {
      cleanup();
    };
  }, [updatePosition]);
  // Stryker restore all

  const popoverHoverTriggered = useRef<boolean>();

  // Main function to open menu
  // This gets canceled if close is called before the promise is resolved
  // Ex) Allows cursor jump from invoker to menu
  //     without closing
  const openMenu = useCallback(async () => {
    popoverHoverTriggered.current = true;
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (
      !popoverHoverTriggered.current ||
      !menuRef.current ||
      !childrenRef.current
    )
      return;

    // Show the menu but keep it transparent
    menuRef.current.style.opacity = "0";
    safeShowPopover(menuRef.current);

    // Position the menu before showing it
    if (!invokerRef.current || !menuRef.current) return;

    await computePosition(invokerRef.current, menuRef.current, {
      placement: "right-start",
      middleware: [
        offset({
          mainAxis: 0,
          alignmentAxis: -4,
        }),
        flip({
          fallbackStrategy: "bestFit",
          fallbackPlacements: fallbackArr() as Placement[],
        }),
        shift({
          mainAxis: !menuContext?.disableAutoHeight,
          crossAxis: false,
          limiter: limitShift({
            mainAxis: !menuContext?.disableAutoHeight,
          }),
        }),
      ],
    }).then(({ x, y }) => {
      if (!menuRef.current) return;
      Object.assign(menuRef.current.style, {
        left: `${x}px`,
        top: `${y + 8}px`,
      });
    });

    setOpenState(true);
    const focusables = Array.from(
      childrenRef.current.querySelectorAll(
        `#scroller-${uid.replace(/:/g, "")} > button, #scroller-${uid.replace(/:/g, "")} > div[data-nested="true"] > button, #scroller-${uid.replace(/:/g, "")} > a[href]`,
      ),
    ) as HTMLElement[];
    setFocusItems(focusables);

    // Make the menu visible with a short delay to ensure smooth transition
    requestAnimationFrame(() => {
      if (!menuRef.current) return;
      menuRef.current.style.opacity = "1";
    });
  }, [uid, fallbackArr, menuContext?.disableAutoHeight]);

  // Main function to close menu
  // This gets canceled if close is called before the promise is resolved
  // Ex) Allows menu to be kept closed when cursor
  //     moves out of the invoker and menu
  const closeMenu = useCallback(async (fn?: () => void) => {
    popoverHoverTriggered.current = false;
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (popoverHoverTriggered.current || !menuRef.current) return;
    fn?.();
    safeHidePopover(menuRef.current);
    setOpenState(false);
  }, []);

  const isControlled = typeof open !== "undefined";

  // Updating openness for controlled
  const initialRender = useRef(true);
  useEffect(() => {
    if (!isControlled) return;
    if (initialRender.current === true) {
      if (open && menuRef.current) {
        openMenu();
      }
      initialRender.current = false;
      return;
    }
    setOpenState(open);
    if (open) {
      openMenu();
    } else {
      closeMenu();
    }
  }, [closeMenu, isControlled, open, openMenu]);

  useEffect(() => {
    if (!containerRef.current) return;
    const nestedContainer = containerRef.current;
    nestedContainer.addEventListener("focusin", (_e) => {
      openMenu();
    });
    nestedContainer.addEventListener("focusout", () => closeMenu());
    return () => {
      nestedContainer.removeEventListener("focusin", () => openMenu());
      nestedContainer.removeEventListener("focusout", () => closeMenu());
    };
  }, [closeMenu, openMenu]);

  const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!openState || !childrenRef.current) return;

    const currentActiveIndex = focusItems?.findIndex(
      (item) => item.id === getActiveElement(document)?.id,
    );

    const firstItem = focusItems?.[0] as HTMLElement;
    const lastItem = focusItems?.[focusItems.length - 1] as HTMLElement;

    if (e.code === "Escape") {
      menuContext?.closeRootMenu();
    }
    if (e.code === "ArrowLeft") {
      e.stopPropagation();
      e.preventDefault();
      invokerRef.current?.focus();
      closeMenu();
    }
    if (e.code === "ArrowDown") {
      e.stopPropagation();
      if (currentActiveIndex === (focusItems?.length ?? 0) - 1) {
        firstItem.focus();
        return;
      }
      const nextItem = focusItems?.[
        (currentActiveIndex ?? 0) + 1
      ] as HTMLElement;
      nextItem.focus();
      return;
    }
    if (e.code === "ArrowUp") {
      e.stopPropagation();
      e.preventDefault();
      if (currentActiveIndex === 0) {
        lastItem.focus();
        return;
      }
      const prevItem = focusItems?.[
        (currentActiveIndex ?? 0) - 1
      ] as HTMLElement;
      prevItem.focus();
      return;
    }
  };

  const onFocusHandler = () => {
    setTabIndex(0);
  };
  const onBlurHandler = () => {
    setTabIndex(-1);
  };

  const MenuButtonOnKeyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.code === "ArrowRight") {
      e.stopPropagation();
      e.preventDefault();
      openMenu();
      const firstItem = menuRef.current?.querySelectorAll(
        `#scroller-${uid.replace(/:/g, "")} > button, #scroller-${uid.replace(/:/g, "")} > a[href]`,
      )[0];
      (firstItem as HTMLButtonElement).focus();
    }
  };

  const MenuButtonOnClick = () => {
    if (isControlled) return;
    openMenu();
  };

  const itemCx = cx([styles["item"]], className);

  return (
    <div ref={containerRef} data-nested="true">
      <button
        role="menuitem"
        data-anv="menu-item"
        data-state={openState ? "open" : "close"}
        ref={invokerRef}
        id={`menuitem-${uid.replace(/:/g, "")}`}
        aria-expanded={openState}
        onMouseEnter={(e) => {
          e.currentTarget.focus();
        }}
        onKeyDown={MenuButtonOnKeyDownHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        onClick={MenuButtonOnClick}
        aria-haspopup={true}
        aria-controls={`menu-${uid.replace(/:/g, "")}`}
        className={itemCx}
        tabIndex={tabIndex}
      >
        {icon ? <Icon inherit aria-hidden svg={icon} /> : null}
        {label}
        <div style={{ flexGrow: 1 }} />
        <Icon aria-hidden svg={Next} />
      </button>
      <div
        popover="auto"
        id={`menu-${uid.replace(/:/g, "")}`}
        data-anv="menu-content"
        className={styles.content}
        ref={menuRef}
        role="presentation"
        onKeyDown={onKeyDownHandler}
      >
        {arrowStart && (
          <div className={styles["arrow-up"]} aria-hidden>
            <Icon svg={Up} size="xlarge" className={styles["arrow-icon"]} />
          </div>
        )}
        <div
          className={styles.scroller}
          tabIndex={-1}
          ref={childrenRef}
          style={{
            position: "relative",
            maxHeight: menuHeight ?? "inherit",
          }}
          onScroll={(e: MouseEvent<HTMLDivElement>) =>
            setScrollTop(e.currentTarget.scrollTop)
          }
          role="menu"
          id={`scroller-${uid.replace(/:/g, "")}`}
        >
          {children}
        </div>
        {arrowEnd && (
          <div className={styles["arrow-down"]} aria-hidden>
            <Icon svg={Down} size="xlarge" className={styles["arrow-icon"]} />
          </div>
        )}
      </div>
    </div>
  );
};
